import React, { useState, useEffect, useContext, ReactNode } from "react";
import * as cognito from "@/libs/cognito";
import jwt_decode, { JwtPayload } from "jwt-decode";
import { useRollbar } from "@rollbar/react";
import { NewPasswordRequired, getCurrentUser } from "@/libs/cognito";
import { setLoginSource, LoginSource } from "@/libs/loginSource";
import { clearAegisTokens, refreshAegisTokens } from "@/libs/aegisSession";
import config from "@/libs/config";
import { useApolloClient } from "@apollo/client";
import {
  SignOutDocument,
  ChangePasswordDocument,
  GetSessionDocument,
  SendForgotPasswordMailDocument,
  UpdatePasswordWithTokenDocument,
} from "@/generated/graphql";

export enum AuthStatus {
  Loading,
  SignedIn,
  SignedOut,
}

export type SessionInfo = {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
};

export type UserInfo = {
  username?: string;
  email?: string;
  emailVerified?: boolean;
  sub?: string;
  authority?: string;
  userType?: string;
  xCompanyID?: string;
  preferredUsername?: string;
};

export interface IAuth {
  sessionInfo?: SessionInfo;
  authStatus?: AuthStatus;
  userInfo?: UserInfo;
  signIn?: any;
  signOut?: (callback?: () => void) => void;
  verifyCode?: any;
  getSession?: any;
  sendCode?: any;
  forgotPassword?: any;
  changePassword?: any;
  completeNewPassword?: any;
  newPasswordRequired?: any;
  setSignInUserSession?: (session: SessionInfo) => Promise<void>;
  fixSignedOutState?: () => boolean;
  isCompanyInfoRegistered?: any;
  setCompanyInfoRegistered?: () => void;
  isResignedTermsOfService?: any;
  setResignedTermsOfService?: () => void;
}

const defaultState: IAuth = {
  sessionInfo: {},
  userInfo: {},
  authStatus: AuthStatus.Loading,
  newPasswordRequired: false,
};

const sessionUserKey = "session-user";
const sessionUserValuePrefix = config.USER_POOL_ID + ".";

export const getSessionUser = (): string => {
  return window.localStorage.getItem(sessionUserKey);
};

// 別タブでのログアウト操作などによってuserPool.Storage からセッションが削除された状態を判定する
const isSignedOut = (
  username: string,
  email: string,
  preferredUsername: string
): boolean => {
  const signedUser = getSessionUser();
  if (!signedUser) {
    return true;
  }
  return (
    signedUser != sessionUserValuePrefix + username?.toLowerCase() &&
    signedUser != sessionUserValuePrefix + email?.toLowerCase() &&
    signedUser != sessionUserValuePrefix + preferredUsername?.toLowerCase()
  ); // signIn() で保存したuser 情報と一致しない場合false
};

function setSessionUser(username: string) {
  window.localStorage.setItem(
    sessionUserKey,
    sessionUserValuePrefix + username.toLowerCase()
  );
}

export const AuthContext = React.createContext(defaultState);

export const AuthIsSignedIn: React.FunctionComponent = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { authStatus }: IAuth = useContext(AuthContext);

  return <>{authStatus === AuthStatus.SignedIn ? children : null}</>;
};

export const AuthIsNotSignedIn: React.FunctionComponent = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { authStatus }: IAuth = useContext(AuthContext);

  return <>{authStatus === AuthStatus.SignedOut ? children : null}</>;
};

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { rollbar } = useRollbar();
  const apolloClient = useApolloClient();
  const [authStatus, setAuthStatus] = useState(AuthStatus.Loading);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo>({});
  const [userInfo, setUserInfo] = useState<UserInfo>({});
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [isCompanyInfoRegistered, setIsCompanyInfoRegistered] = useState(false);
  const [isResignedTermsOfService, setIsResignedTermsOfService] =
    useState(false);
  useEffect(() => {
    // Cognito path
    async function getSessionInfo() {
      if (localStorage.getItem("aegis_id_token")) {
        const idToken = localStorage.getItem("aegis_id_token");
        const expiresAt = Number(
          localStorage.getItem("aegis_expires_at") || "0"
        );
        if (idToken && expiresAt >= Date.now()) {
          try {
            const { data } = await apolloClient.query({
              query: GetSessionDocument,
            });
            setUserInfo({
              username: data.getSession.username,
              email: data.getSession.email,
              emailVerified: data.getSession.emailVerified,
              sub: data.getSession.sub,
              authority: data.getSession.authority as any,
              userType: data.getSession.userType as any,
              xCompanyID: data.getSession.xCompanyID,
              preferredUsername:
                data.getSession.preferredUsername || data.getSession.username,
            });
            setSessionInfo({
              idToken,
              accessToken: localStorage.getItem("aegis_access_token") || "",
              refreshToken: localStorage.getItem("aegis_refresh_token") || "",
            });
            setSessionUser(data.getSession.username);
            setLoginSource(LoginSource.aegis);
            setAuthStatus(AuthStatus.SignedIn);
          } catch (e) {
            clearAegisTokens();
            setAuthStatus(AuthStatus.SignedOut);
          }
        } else {
          // Token expired — attempt silent refresh before signing out.
          const refreshed = await refreshAegisTokens();
          if (refreshed) {
            await getSessionInfo();
            return;
          }
          clearAegisTokens();
          setAuthStatus(AuthStatus.SignedOut);
        }
        return;
      } else {
        try {
          const session: any = await getSession();
          const idToken: string = session.idToken.getJwtToken();
          const jwtPayload = jwt_decode<JwtPayload>(idToken);
          const username = jwtPayload["cognito:username"];
          const email = jwtPayload["email"];
          const preferredUsername = jwtPayload["preferred_username"];
          const sub = jwtPayload["sub"];
          const xCompanyID = jwtPayload["custom:x_company_id"];

          rollbar.configure({
            payload: {
              person: {
                id: sub,
                companyId: xCompanyID,
                bankCode: config.BANK_CODE,
                email: config.STAGE !== "prod" ? email : "",
              },
            },
          });

          if (isSignedOut(username, email, preferredUsername)) {
            // localStorage に値がなければサインイン状態とは扱わずサインアウトする。
            signOut();
            return;
          }

          setSessionInfo({
            accessToken: session.accessToken.jwtToken,
            idToken: idToken,
            refreshToken: session.refreshToken.token,
          });
          setUserInfo({
            username: username,
            email: jwtPayload["email"],
            emailVerified: jwtPayload["email_verified"],
            sub,
            authority: jwtPayload["custom:authority"],
            userType: jwtPayload["custom:user_type"],
            xCompanyID,
            preferredUsername: jwtPayload["preferred_username"],
          });

          setAuthStatus(AuthStatus.SignedIn);
        } catch (err) {
          rollbar.warn("Failed to get session", err);
          window.localStorage.removeItem(sessionUserKey);
          setAuthStatus(AuthStatus.SignedOut);
        }
      }
    }
    getSessionInfo();
  }, []);

  if (authStatus === AuthStatus.Loading) {
    return null;
  }

  // authStatusとlocalStorage の状態が矛盾していた場合サインアウト状態に修正してtrue を返却する
  const fixSignedOutState = (): boolean => {
    // サインイン直後などでuserInfo がset されていない状態は処理しない
    if (authStatus != AuthStatus.SignedIn || !userInfo.username) {
      return false;
    }
    if (
      isSignedOut(userInfo.username, userInfo.email, userInfo.preferredUsername)
    ) {
      window.localStorage.removeItem(sessionUserKey);
      setAuthStatus(AuthStatus.SignedOut);
      return true;
    }
    return false;
  };

  async function applyCognitoSession() {
    const session: any = await cognito.getSession();
    const idToken: string = session.idToken.getJwtToken();
    const jwtPayload = jwt_decode<JwtPayload>(idToken);
    setSessionInfo({
      accessToken: session.accessToken.jwtToken,
      idToken,
      refreshToken: session.refreshToken.token,
    });
    setUserInfo({
      username: jwtPayload["cognito:username"],
      email: jwtPayload["email"],
      emailVerified: jwtPayload["email_verified"],
      sub: jwtPayload["sub"],
      authority: jwtPayload["custom:authority"],
      userType: jwtPayload["custom:user_type"],
      xCompanyID: jwtPayload["custom:x_company_id"],
      preferredUsername: jwtPayload["preferred_username"],
    });
  }

  async function signIn(username: string, password: string) {
    return new Promise(function (resolve, reject) {
      cognito
        .signIn(username, password)
        .then(async (res) => {
          let succeeded = false;
          if (res instanceof NewPasswordRequired) {
            setNewPasswordRequired(true);
          } else {
            succeeded = true;
            setSessionUser(username);
            setLoginSource(LoginSource.mikatano);

            try {
              await applyCognitoSession();
            } catch (_) {
              // non-fatal: session will be populated on next mount
            }

            setAuthStatus(AuthStatus.SignedIn);
          }
          resolve(succeeded);
        })
        .catch((err) => {
          rollbar.warn("Failed to sign in", err);
          window.localStorage.removeItem(sessionUserKey);
          setAuthStatus(AuthStatus.SignedOut);
          setLoginSource(LoginSource.none);
          reject(err);
        });
    });
  }

  function signOut(callback?: () => void) {
    const cleanup = () => {
      window.localStorage.removeItem(sessionUserKey);
      clearAegisTokens();
      setAuthStatus(AuthStatus.SignedOut);
      setLoginSource(LoginSource.none);
      setIsCompanyInfoRegistered(false);
      setIsResignedTermsOfService(false);
      if (callback) callback();
    };

    if (localStorage.getItem("aegis_id_token")) {
      apolloClient
        .mutate({
          mutation: SignOutDocument,
          variables: {
            input: {
              idTokenHint: localStorage.getItem("aegis_id_token") || "",
              postLogoutRedirectUri: window.location.href,
            },
          },
        })
        .then((result) => {
          cleanup();
          const logoutURL = result.data?.signOut?.logoutURL;
          if (logoutURL) window.location.href = logoutURL;
        })
        .catch(() => cleanup());
    } else {
      cognito.signOut(cleanup);
    }
  }

  async function verifyCode(username: string, code: string) {
    await cognito.verifyCode(username, code);
  }

  async function getSession() {
    const session = await cognito.getSession();
    return session;
  }

  // No current callers — kept because future UI could trigger this.
  // If company is migrated, backend SendForgotPasswordMail handles KC routing.
  async function sendCode(username: string) {
    if (localStorage.getItem("aegis_id_token")) {
      await apolloClient.mutate({
        mutation: SendForgotPasswordMailDocument,
        variables: { input: { email: username, app: "" } },
      });
      return;
    }
    await cognito.sendCode(username);
  }

  // No current callers — kept because future UI could trigger this.
  // If company is migrated, backend UpdatePasswordWithToken handles KC routing.
  async function forgotPassword(
    username: string,
    code: string,
    password: string
  ) {
    if (localStorage.getItem("aegis_id_token")) {
      await apolloClient.mutate({
        mutation: UpdatePasswordWithTokenDocument,
        variables: { token: code, password },
      });
      return;
    }
    await cognito.forgotPassword(username, code, password);
  }

  // No current callers — kept because future UI could trigger this.
  async function changePassword(oldPassword: string, newPassword: string) {
    if (localStorage.getItem("aegis_id_token")) {
      await apolloClient.mutate({
        mutation: ChangePasswordDocument,
        variables: { input: { oldPassword, newPassword } },
      });
      return;
    }
    await cognito.changePassword(oldPassword, newPassword);
  }

  async function completeNewPassword(newPassword: string) {
    return new Promise(function (resolve, reject) {
      cognito
        .completeNewPasswordChallenge(newPassword)
        .then(async (res) => {
          const user = getCurrentUser();
          setSessionUser(user.getUsername());

          try {
            await applyCognitoSession();
          } catch (_) {
            // non-fatal: session will be populated on next mount
          }

          setAuthStatus(AuthStatus.SignedIn);
          setNewPasswordRequired(false);
          resolve(res);
        })
        .catch((err) => {
          rollbar.warn("Failed to complete new password", err);
          window.localStorage.removeItem(sessionUserKey);
          setAuthStatus(AuthStatus.SignedOut);
          reject(err);
        });
    });
  }

  async function setSignInUserSession(session: SessionInfo) {
    await cognito.setSignInUserSession({
      AccessToken: session.accessToken,
      IdToken: session.idToken,
      RefreshToken: session.refreshToken,
    });

    const user = getCurrentUser();
    setSessionUser(user.getUsername());
    await applyCognitoSession();
    setAuthStatus(AuthStatus.SignedIn);
  }

  function setCompanyInfoRegistered() {
    setIsCompanyInfoRegistered(true);
  }

  function setResignedTermsOfService() {
    setIsResignedTermsOfService(true);
  }

  const state: IAuth = {
    authStatus,
    sessionInfo,
    userInfo,
    signIn,
    signOut,
    verifyCode,
    getSession,
    sendCode,
    forgotPassword,
    changePassword,
    completeNewPassword,
    newPasswordRequired,
    setSignInUserSession,
    fixSignedOutState,
    isCompanyInfoRegistered,
    setCompanyInfoRegistered,
    isResignedTermsOfService,
    setResignedTermsOfService,
  };

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
